import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  RefreshCw,
  Loader2,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Package,
  Printer,
  AlertTriangle,
  AlertCircle,
  PackageX,
  Truck,
  SlidersHorizontal,
  Download,
  Clock,
} from 'lucide-react';
import { OrderFilters as OrderFiltersType } from './types';
import { api } from '../../api/client';
import { useIsMobile } from '../../hooks/useIsMobile';
import { buildOrdersQueryParams } from './useOrders';
import { Can, useCan } from '../../lib/rbac';

/* ── Helpers ─────────────────────────────────────────────── */

function formatCompact(n: number | null | undefined): string {
  if (n == null) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

/* ── Types ───────────────────────────────────────────────── */

interface OrderFiltersSummary {
  total_orders: number;
  total_revenue: number;
  total_profit: number;
  pending_purchases: number;
  open_claims: number;
  not_delivered_count: number;
  delayed_count: number;
  delivery_issues_count: number;
  ready_to_ship_count: number;
  stale_released_count: number;
  unread_messages_count: number;
}

interface OrderFiltersProps {
  filters: OrderFiltersType;
  onChange: (partial: Partial<OrderFiltersType>) => void;
  summary: OrderFiltersSummary;
  onRefresh: () => void;
  loading: boolean;
  sellerId: number;
}

/* ── Summary bar ─────────────────────────────────────────── */

function SummaryBar({
  summary,
  pendingOnly,
  onPendingOnlyChange,
  staleReleasedOnly,
  onStaleReleasedOnlyChange,
}: {
  summary: OrderFiltersSummary;
  pendingOnly: boolean;
  onPendingOnlyChange: (v: boolean) => void;
  staleReleasedOnly: boolean;
  onStaleReleasedOnlyChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const can = useCan();
  const marginPct =
    summary.total_revenue > 0
      ? ((summary.total_profit / summary.total_revenue) * 100).toFixed(0)
      : '0';

  const pendingHighlight = summary.pending_purchases > 0;

  const stats = [
    {
      icon: <Package size={14} />,
      label: t('orders.filters.orders', '{{count}} orders', { count: summary.total_orders }),
      alwaysShow: true,
      money: false,
    },
    {
      icon: <DollarSign size={14} />,
      label: `${formatCompact(summary.total_revenue)} ${t('orders.filters.revenue', 'revenue')}`,
      alwaysShow: true,
      money: true,
    },
    {
      icon: <TrendingUp size={14} />,
      label: `${formatCompact(summary.total_profit)} ${t('orders.filters.profit', 'profit')} (${marginPct}%)`,
      alwaysShow: false,
      money: true,
    },
    // Sin la llave de dinero, ingresos y ganancia no se muestran.
  ].filter((s) => !s.money || can('global.view_money'));

  const visibleStats = isMobile ? stats.filter(s => s.alwaysShow) : stats;
  const showPending = !isMobile || pendingHighlight;

  return (
    <div className="flex items-center flex-wrap gap-3 sm:gap-4 text-sm text-[var(--color-text-secondary)]">
      {visibleStats.map((s, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          {s.icon}
          {s.label}
        </span>
      ))}
      {showPending && (
        <label
          className={`inline-flex items-center gap-1.5 cursor-pointer select-none ${
            pendingHighlight ? 'text-[var(--color-warning)] font-medium' : ''
          }`}
          title={t('orders.filters.pendingOnly', 'Pending only')}
        >
          <input
            type="checkbox"
            checked={pendingOnly}
            onChange={(e) => onPendingOnlyChange(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
          />
          <ShoppingCart size={14} />
          {t('orders.filters.pendingPurchases', '{{count}} pending purchases', {
            count: summary.pending_purchases,
          })}
        </label>
      )}
      {summary.stale_released_count > 0 && (
        <label
          className="inline-flex items-center gap-1.5 cursor-pointer select-none text-[var(--color-error)] font-medium"
          title="Released hace más de 24h y ML todavía no las procesó"
        >
          <input
            type="checkbox"
            checked={staleReleasedOnly}
            onChange={(e) => onStaleReleasedOnlyChange(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-[var(--color-border)] text-[var(--color-error)] focus:ring-[var(--color-error)]"
          />
          <AlertCircle size={14} />
          {summary.stale_released_count} sin procesar MailAmericas
        </label>
      )}
    </div>
  );
}

/* ── Main component ──────────────────────────────────────── */

export function OrderFiltersBar({
  filters,
  onChange,
  summary,
  onRefresh,
  loading,
  sellerId,
}: OrderFiltersProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [pendingLabels, setPendingLabels] = useState(0);
  const [merging, setMerging] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Download the current filtered view as a CSV (server builds it from the same filters).
  const handleDownloadCsv = async () => {
    setDownloading(true);
    try {
      const params = buildOrdersQueryParams(filters, sellerId);
      const blob = await api.getBlob(`/v3/orders/unified/export.csv?${params.toString()}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ordenes-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[orders] CSV download failed', err);
      alert('No se pudo generar el informe. Probá de nuevo.');
    } finally {
      setDownloading(false);
    }
  };

  // Load pending label count
  useEffect(() => {
    api.get(`/labels/pending-count?seller_id=${sellerId}`).then((res: any) => {
      if (res.success) setPendingLabels(res.data?.count || 0);
    }).catch(() => {});
  }, [sellerId, loading]); // refresh count when data refreshes

  const handleMergeAll = useCallback(async () => {
    if (merging) return;
    setMerging(true);
    try {
      const res = await api.post('/labels/merge', { seller_id: sellerId }) as any;
      if (res.success && res.data?.url) {
        window.open(res.data.url, '_blank');
      } else if (res.success && res.data?.count === 0) {
        alert('No hay etiquetas pendientes para mergear');
      }
      setPendingLabels(0);
      onRefresh();
    } catch (err: any) {
      alert(`Error merging labels: ${err.message || 'Unknown error'}`);
    } finally {
      setMerging(false);
    }
  }, [sellerId, merging, onRefresh]);

  const selectClasses =
    'h-9 px-3 text-sm rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] outline-none focus:border-[var(--color-primary)] transition-colors';
  const inputClasses =
    'h-9 px-3 text-sm rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] outline-none focus:border-[var(--color-primary)] transition-colors';

  return (
    <div className="space-y-3">
      {/* Summary */}
      <SummaryBar
        summary={summary}
        pendingOnly={filters.pendingOnly}
        onPendingOnlyChange={(v) => onChange({ pendingOnly: v })}
        staleReleasedOnly={filters.staleReleasedOnly}
        onStaleReleasedOnlyChange={(v) => onChange({ staleReleasedOnly: v })}
      />

      {/* Filters row */}
      <div className="flex items-center flex-wrap gap-2">
        {/* Status — always visible */}
        <select
          value={filters.shipSubstatus ? filters.shipSubstatus : filters.notDeliveredAll ? 'not_delivered' : filters.unreadOnly ? 'unread_messages' : filters.readyToShipOnly ? 'ready_to_ship' : filters.status}
          onChange={(e) => {
            const val = e.target.value;
            if (val === 'ready_to_ship') {
              onChange({ status: '', readyToShipOnly: true, unreadOnly: false, notDeliveredAll: false, shipSubstatus: '' });
            } else if (val === 'unread_messages') {
              onChange({ status: '', readyToShipOnly: false, unreadOnly: true, notDeliveredAll: false, shipSubstatus: '' });
            } else if (val === 'not_delivered') {
              onChange({ status: '', readyToShipOnly: false, unreadOnly: false, notDeliveredAll: true, shipSubstatus: '' });
            } else if (val === 'fraudulent' || val === 'receiver_insufficient_quota') {
              onChange({ status: '', readyToShipOnly: false, unreadOnly: false, notDeliveredAll: false, shipSubstatus: val });
            } else {
              onChange({ status: val, readyToShipOnly: false, unreadOnly: false, notDeliveredAll: false, shipSubstatus: '' });
            }
          }}
          className={`${selectClasses} flex-1 sm:flex-none`}
        >
          <option value="">{t('orders.filters.allStatuses', 'All Statuses')}</option>
          <option value="paid">{t('orders.filters.paid', 'Paid')}</option>
          <option value="ready_to_ship">{t('orders.filters.readyToShip', 'Ready to Ship')}</option>
          {summary.unread_messages_count > 0 && (
            <option value="unread_messages">
              {t('orders.filters.unreadMessages', 'Mensajes No Leídos')} ({summary.unread_messages_count})
            </option>
          )}
          <option value="cancelled">{t('orders.filters.cancelled', 'Cancelled')}</option>
          <option value="not_delivered">{t('orders.filters.notDelivered', 'Not Delivered')}</option>
          <option value="fraudulent">{t('orders.filters.fraudulent', 'Fraudulentas')}</option>
          <option value="receiver_insufficient_quota">
            {t('orders.filters.receiverQuota', 'Retenidas por cupo del comprador')}
          </option>
        </select>

        {/* Search — always visible */}
        <div className="relative flex-1 sm:flex-none">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] pointer-events-none"
          />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => onChange({ search: e.target.value })}
            placeholder={t('orders.filters.search', 'Search orders...')}
            className={`${inputClasses} pl-8 w-full sm:w-[200px]`}
          />
        </div>

        {/* Mobile: toggle button for advanced filters */}
        {isMobile && (
          <button
            onClick={() => setFiltersExpanded(v => !v)}
            className={`h-9 px-2.5 inline-flex items-center gap-1 text-sm rounded-[var(--border-radius)] border transition-colors ${
              filtersExpanded
                ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                : 'border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text-muted)]'
            }`}
          >
            <SlidersHorizontal size={14} />
          </button>
        )}

        {/* Refresh — always visible */}
        <button
          onClick={onRefresh}
          disabled={loading}
          className="h-9 px-3 inline-flex items-center gap-1.5 text-sm font-medium rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50 transition-colors"
        >
          {loading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <RefreshCw size={14} />
          )}
          <span className="hidden sm:inline">{t('orders.filters.refresh', 'Refresh')}</span>
        </button>

        {/* Download CSV — export the current filtered view */}
        <Can cap="reports.export">
          <button
            onClick={handleDownloadCsv}
            disabled={downloading}
            title={t('orders.filters.downloadCsv', 'Descargar CSV')}
            className="h-9 px-3 inline-flex items-center gap-1.5 text-sm font-medium rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50 transition-colors"
          >
            {downloading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            <span className="hidden sm:inline">{t('orders.filters.downloadCsv', 'Descargar CSV')}</span>
          </button>
        </Can>

        {/* Advanced filters — hidden on mobile unless expanded */}
        {(!isMobile || filtersExpanded) && (
          <>
            {/* Site */}
            <select
              value={filters.site}
              onChange={(e) => onChange({ site: e.target.value })}
              className={`${selectClasses} flex-1 sm:flex-none`}
            >
              <option value="">{t('orders.filters.allSites', 'All Sites')}</option>
              <option value="MLA">MLA</option>
              <option value="MLM">MLM</option>
              <option value="MLC">MLC</option>
              <option value="MCO">MCO</option>
              <option value="MLB">MLB</option>
            </select>

            {/* Date from */}
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => onChange({ dateFrom: e.target.value })}
              className={`${inputClasses} w-full sm:w-[140px]`}
              placeholder={t('orders.filters.from', 'From')}
            />

            {/* Date to */}
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => onChange({ dateTo: e.target.value })}
              className={`${inputClasses} w-full sm:w-[140px]`}
              placeholder={t('orders.filters.to', 'To')}
            />

            {/* Warehouse status */}
            <select
              value={filters.whStatus}
              onChange={(e) => onChange({ whStatus: e.target.value })}
              className={`${selectClasses} flex-1 sm:flex-none`}
            >
              <option value="">{t('orders.filters.allWarehouse', 'All WH')}</option>
              <option value="none">{t('orders.filters.whNone', 'Not in WH')}</option>
              <option value="in_warehouse">{t('orders.filters.whAny', 'In WH')}</option>
              <option value="received">{t('orders.filters.whReceived', 'WH: Received')}</option>
              <option value="packed">{t('orders.filters.whPacked', 'WH: Packed')}</option>
              <option value="closed">{t('orders.filters.whClosed', 'WH: Closed')}</option>
            </select>

          </>
        )}

        {/* Open Claims — always visible (important action) */}
        {summary.open_claims > 0 && (
          <button
            onClick={() => onChange({ claimsOnly: !filters.claimsOnly })}
            className={`h-9 px-3 inline-flex items-center gap-1.5 text-sm font-medium rounded-[var(--border-radius)] transition-opacity ${
              filters.claimsOnly
                ? 'bg-[var(--color-error)] text-white'
                : 'bg-[var(--color-error-light)] text-[var(--color-error)] hover:opacity-90'
            }`}
          >
            <AlertTriangle size={14} />
            <span className="hidden sm:inline">Claims</span>
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
              filters.claimsOnly ? 'bg-white/20 text-white' : 'bg-[var(--color-error)] text-white'
            }`}>
              {summary.open_claims}
            </span>
          </button>
        )}

        {/* Not Delivered — always visible (important action) */}
        {summary.not_delivered_count > 0 && (
          <button
            onClick={() => onChange({ notDeliveredOnly: !filters.notDeliveredOnly })}
            className={`h-9 px-3 inline-flex items-center gap-1.5 text-sm font-medium rounded-[var(--border-radius)] transition-opacity ${
              filters.notDeliveredOnly
                ? 'bg-orange-600 text-white'
                : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 hover:opacity-90'
            }`}
          >
            <PackageX size={14} />
            <span className="hidden sm:inline">Not Delivered</span>
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
              filters.notDeliveredOnly ? 'bg-white/20 text-white' : 'bg-orange-600 text-white'
            }`}>
              {summary.not_delivered_count}
            </span>
          </button>
        )}

        {/* Atrasados — en viaje en ML con fecha estimada vencida (+3 días), sin resolver */}
        {summary.delayed_count > 0 && (
          <button
            onClick={() => onChange({ delayedOnly: !filters.delayedOnly })}
            className={`h-9 px-3 inline-flex items-center gap-1.5 text-sm font-medium rounded-[var(--border-radius)] transition-opacity ${
              filters.delayedOnly
                ? 'bg-orange-600 text-white'
                : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 hover:opacity-90'
            }`}
            title="Envíos en viaje cuya fecha estimada de entrega venció hace más de 3 días"
          >
            <Clock size={14} />
            <span className="hidden sm:inline">{t('orders.filters.delayed', 'Atrasados')}</span>
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
              filters.delayedOnly ? 'bg-white/20 text-white' : 'bg-orange-600 text-white'
            }`}>
              {summary.delayed_count}
            </span>
          </button>
        )}

        {/* Delivery Issues — Amazon-side delivery problems */}
        {summary.delivery_issues_count > 0 && (
          <button
            onClick={() => onChange({ deliveryIssuesOnly: !filters.deliveryIssuesOnly })}
            title={t('orders.filters.deliveryIssuesTitle', 'Atrasados, sin updates, re-agendados o cancelados por Amazon')}
            className={`h-9 px-3 inline-flex items-center gap-1.5 text-sm font-medium rounded-[var(--border-radius)] transition-opacity ${
              filters.deliveryIssuesOnly
                ? 'bg-amber-600 text-white'
                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 hover:opacity-90'
            }`}
          >
            <Truck size={14} />
            <span className="hidden sm:inline">Delivery Issues</span>
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
              filters.deliveryIssuesOnly ? 'bg-white/20 text-white' : 'bg-amber-600 text-white'
            }`}>
              {summary.delivery_issues_count}
            </span>
          </button>
        )}

        {/* Merge All Labels */}
        {pendingLabels > 0 && (
          <button
            onClick={handleMergeAll}
            disabled={merging}
            className="h-9 px-3 inline-flex items-center gap-1.5 text-sm font-medium rounded-[var(--border-radius)] bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {merging ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Printer size={14} />
            )}
            <span className="hidden sm:inline">Merge Labels</span>
            <span className="bg-white/20 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
              {pendingLabels}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
